/**
 * Coaching Context Banner
 *
 * Displays coaching preparation context when a user starts a simulation
 * after completing a coaching session.
 */

import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  BookOpen,
  Target,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  ArrowRight,
  Lightbulb,
  AlertCircle
} from 'lucide-react';
import { getCoachingContextForSimulation, type CoachingContext } from '@/lib/learningLoop/contextService';

interface CoachingContextBannerProps {
  className?: string;
}

export function CoachingContextBanner({ className = '' }: CoachingContextBannerProps) {
  const [searchParams] = useSearchParams();
  const [context, setContext] = useState<CoachingContext | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);

  const coachingSessionId = searchParams.get('coaching');
  const pathwayId = searchParams.get('pathway');
  const moduleId = searchParams.get('module');

  useEffect(() => {
    if (coachingSessionId) {
      const storedContext = getCoachingContextForSimulation(coachingSessionId);
      if (storedContext) {
        setContext(storedContext);
      }
    }
  }, [coachingSessionId]);

  // Don't render if no coaching context
  if (!coachingSessionId && !pathwayId) return null;

  // Pathway context banner
  if (pathwayId && moduleId) {
    const pathwayContext = sessionStorage.getItem('pathway_context');
    let pathwayName = 'Learning Pathway';

    try {
      if (pathwayContext) {
        const parsed = JSON.parse(pathwayContext);
        pathwayName = parsed.pathwayName || 'Learning Pathway';
      }
    } catch {}

    return (
      <Card className={`mb-6 overflow-hidden border-blue-200 dark:border-blue-800 bg-gradient-to-r from-blue-50 to-transparent dark:from-blue-950/30 ${className}`}>
        <div className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-full">
              <BookOpen className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-blue-800 dark:text-blue-300">
                  Learning Pathway Module
                </h3>
                <Badge variant="secondary" className="text-xs">
                  {pathwayName}
                </Badge>
              </div>
              <p className="text-sm text-blue-600 dark:text-blue-400">
                Complete this practice session to progress in your learning pathway.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              asChild
              className="border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-300"
            >
              <Link to={`/learning/pathway/${pathwayId}`}>
                View Pathway
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Coaching context banner
  if (!context) {
    return (
      <Card className={`mb-6 overflow-hidden border-amber-200 dark:border-amber-800 bg-gradient-to-r from-amber-50 to-transparent dark:from-amber-950/30 ${className}`}>
        <div className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/40 rounded-full">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-amber-800 dark:text-amber-300">
                Coaching Context Not Found
              </h3>
              <p className="text-sm text-amber-600 dark:text-amber-400">
                Your preparation context has expired. You can still practice, but won't have access to your coaching notes.
              </p>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className={`mb-6 overflow-hidden border-green-200 dark:border-green-800 ${className}`}>
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <div className="p-4 bg-gradient-to-r from-green-50 to-transparent dark:from-green-950/30">
          <CollapsibleTrigger asChild>
            <div className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-full">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="font-medium text-green-800 dark:text-green-300 flex items-center gap-2">
                    Prepared with {context.templateName}
                    {context.readinessScore && (
                      <Badge className="bg-green-500 text-white">
                        {context.readinessScore}% Ready
                      </Badge>
                    )}
                  </h3>
                  <p className="text-sm text-green-600 dark:text-green-400">
                    Your coaching preparation is ready. Click to review your notes.
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" className="text-green-700 dark:text-green-300">
                {isExpanded ? (
                  <>
                    Hide <ChevronUp className="w-4 h-4 ml-1" />
                  </>
                ) : (
                  <>
                    Show <ChevronDown className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </CollapsibleTrigger>
        </div>

        <CollapsibleContent>
          <div className="px-4 pb-4 pt-2 space-y-4">
            {/* Prospect/Context Info */}
            {(context.prospectName || context.companyName) && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Context
                </h4>
                <div className="text-sm space-y-1">
                  {context.prospectName && (
                    <p><span className="text-muted-foreground">Contact:</span> {context.prospectName}</p>
                  )}
                  {context.companyName && (
                    <p><span className="text-muted-foreground">Company:</span> {context.companyName}</p>
                  )}
                </div>
              </div>
            )}

            {/* Objectives */}
            {context.objectives && context.objectives.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Your Objectives
                </h4>
                <ul className="text-sm space-y-1">
                  {context.objectives.slice(0, 3).map((obj, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{obj}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Key Talking Points */}
            {context.keyTalkingPoints && context.keyTalkingPoints.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4" />
                  Key Talking Points
                </h4>
                <ul className="text-sm space-y-1">
                  {context.keyTalkingPoints.slice(0, 3).map((point, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-primary font-bold">•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Anticipated Objections */}
            {context.anticipatedObjections && context.anticipatedObjections.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Be Ready For
                </h4>
                <div className="flex flex-wrap gap-2">
                  {context.anticipatedObjections.slice(0, 4).map((obj, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {obj}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Link to full coaching results */}
            <div className="pt-2 border-t">
              <Button
                variant="link"
                size="sm"
                asChild
                className="p-0 h-auto text-green-700 dark:text-green-400"
              >
                <Link to={`/coaching/results/${context.sessionId}`}>
                  View Full Coaching Document
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Learning Loop Progress Indicator */}
      <div className="px-4 py-2 bg-green-100/50 dark:bg-green-900/20 border-t border-green-200 dark:border-green-800">
        <div className="flex items-center justify-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
              <CheckCircle className="w-3 h-3 text-white" />
            </div>
            <span className="font-medium text-green-700 dark:text-green-400">PREPARE</span>
          </div>
          <ArrowRight className="w-3 h-3 text-green-600" />
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center animate-pulse">
              <Target className="w-3 h-3 text-white" />
            </div>
            <span className="font-medium text-green-700 dark:text-green-400">PRACTICE</span>
          </div>
          <ArrowRight className="w-3 h-3 text-muted-foreground" />
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center">
              <MessageSquare className="w-3 h-3 text-muted-foreground" />
            </div>
            <span className="text-muted-foreground">REFLECT</span>
          </div>
        </div>
      </div>
    </Card>
  );
}
